import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  onSnapshot, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref as storageRef, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import {
  FileText,
  Video,
  Link,
  Upload,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Download,
  Eye,
  Tag,
  Clock,
  Users,
  FolderOpen,
  File,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const ResourceManager = () => {
  const { currentUser } = useAuth();
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    tags: [],
    department: 'all'
  });

  // New resource state
  const [newResource, setNewResource] = useState({
    title: '',
    type: 'file',
    description: '',
    tags: [],
    linkedTemplates: [],
    department: '',
    file: null,
    url: ''
  });

  // Get orgId from current user (use id as fallback)
  const getOrgId = () => {
    return currentUser?.orgId || currentUser?.id || null;
  };

  // Debug current user
  useEffect(() => {
    console.log('Current User Object:', currentUser);
    if (currentUser) {
      console.log('User ID:', currentUser.id);
      console.log('Org ID:', getOrgId());
    }
  }, [currentUser]);

  // Subscribe to resources
  useEffect(() => {
    const orgId = getOrgId();
    
    if (!orgId) {
      console.log('No user or orgId, skipping resource subscription');
      return;
    }

    console.log('Subscribing to resources for orgId:', orgId);

    const q = query(
      collection(db, 'sopResources'),
      where('orgId', '==', orgId)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        console.log('Resources snapshot received:', snapshot.size, 'documents');
        const resourcesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setResources(resourcesList);
        setFilteredResources(resourcesList);
      },
      (error) => {
        console.error('Error fetching resources:', error);
        alert('שגיאה בטעינת המשאבים');
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Apply filters
  useEffect(() => {
    let filtered = [...resources];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(resource =>
        resource.title.toLowerCase().includes(searchLower) ||
        resource.description?.toLowerCase().includes(searchLower) ||
        (resource.tags && resource.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(resource => resource.type === filters.type);
    }

    // Tags filter
    if (filters.tags.length > 0) {
      filtered = filtered.filter(resource =>
        resource.tags && filters.tags.every(tag => resource.tags.includes(tag))
      );
    }

    // Department filter
    if (filters.department !== 'all') {
      filtered = filtered.filter(resource => resource.department === filters.department);
    }

    setFilteredResources(filtered);
  }, [resources, filters]);

  // Get all unique tags
  const allTags = [...new Set(resources.flatMap(r => r.tags || []))];

  // Upload resource
  const handleUpload = async (e) => {
    e.preventDefault();
    
    const orgId = getOrgId();
    
    console.log('Upload form submitted');
    console.log('Current User:', currentUser);
    console.log('Using orgId:', orgId);
    console.log('New Resource:', newResource);
    
    if (!orgId) {
      alert('משתמש לא מחובר או חסר מזהה ארגון');
      console.error('Missing user or orgId:', currentUser);
      return;
    }

    // Validate form
    if (!newResource.title.trim()) {
      alert('חובה להזין כותרת');
      return;
    }

    if (newResource.type === 'file' && !newResource.file) {
      alert('חובה לבחור קובץ');
      return;
    }

    if ((newResource.type === 'link' || newResource.type === 'embed') && !newResource.url.trim()) {
      alert('חובה להזין כתובת URL או קוד הטמעה');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      let fileUrl = newResource.url;
      let storagePath = '';
      let mimeType = '';
      let fileData = null;

      // פתרון מלא להעלאת קבצים עם CORS
      if (newResource.type === 'file' && newResource.file) {
        const file = newResource.file;
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        storagePath = `orgs/${orgId}/resources/${fileName}`;
        
        console.log('Starting file upload to:', storagePath);
        
        try {
          // יצירת reference לקובץ ב-Storage
          const fileRef = storageRef(storage, storagePath);
          
          // התקדמות ההעלאה
          setUploadProgress(25);
          
          // העלאה פשוטה עם uploadBytes
          await uploadBytes(fileRef, file, {
            contentType: file.type,
            customMetadata: {
              uploadedBy: currentUser.email || '',
              orgId: orgId,
              timestamp: timestamp.toString()
            }
          });
          
          setUploadProgress(75);
          
          // קבלת URL להורדה
          fileUrl = await getDownloadURL(fileRef);
          
          setUploadProgress(100);
          mimeType = file.type;
          console.log('File uploaded successfully:', fileUrl);
          
        } catch (uploadError) {
          console.error('Storage upload error:', uploadError);
          
          // פתרון חלופי: המרה ל-base64 ושמירה ב-Firestore
          if (uploadError.code === 'storage/unauthorized' || 
              uploadError.message?.includes('CORS') || 
              uploadError.message?.includes('blocked')) {
            
            const reader = new FileReader();
            fileData = await new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            
            alert('בעיית CORS - הקובץ יישמר בשיטה חלופית');
            fileUrl = ''; // נשתמש ב-fileData במקום
            mimeType = file.type;
            setUploadProgress(100);
          } else {
            throw uploadError;
          }
        }
      }
      
      // Create resource document
      const resourceData = {
        orgId: orgId,
        title: newResource.title.trim(),
        type: newResource.type,
        description: newResource.description.trim(),
        tags: newResource.tags || [],
        linkedTemplates: newResource.linkedTemplates || [],
        department: newResource.department || '',
        version: 1,
        createdBy: currentUser.uid || currentUser.userId || currentUser.id || 'unknown',
        createdByEmail: currentUser.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (newResource.type === 'file') {
        if (fileUrl) {
          // העלאה רגילה הצליחה
          resourceData.storagePath = storagePath;
          resourceData.url = fileUrl;
        } else if (fileData) {
          // שמירת הקובץ כ-data URL
          resourceData.fileData = fileData;
          resourceData.isDataUrl = true;
        }
        resourceData.mime = mimeType;
        resourceData.fileName = newResource.file.name;
        resourceData.fileSize = newResource.file.size;
      } else if (newResource.type === 'link') {
        resourceData.url = newResource.url.trim();
      } else if (newResource.type === 'embed') {
        resourceData.embedCode = newResource.url.trim();
      }

      console.log('Saving resource data:', resourceData);
      
      const docRef = await addDoc(collection(db, 'sopResources'), resourceData);
      console.log('Resource saved successfully with ID:', docRef.id);

      // Reset form
      setNewResource({
        title: '',
        type: 'file',
        description: '',
        tags: [],
        linkedTemplates: [],
        department: '',
        file: null,
        url: ''
      });
      setShowUploadModal(false);
      setUploadProgress(0);
      
      alert('המשאב הועלה בהצלחה!');
      
    } catch (error) {
      console.error('Detailed error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'שגיאה בהעלאת המשאב';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'אין הרשאות להעלאת משאבים. בדוק את הגדרות Firebase Rules';
      } else if (error.code === 'storage/unauthorized') {
        errorMessage = 'אין הרשאות להעלאת קבצים ל-Storage';
      } else if (error.code === 'storage/unauthenticated') {
        errorMessage = 'יש להתחבר מחדש למערכת';
      } else if (error.message) {
        errorMessage = `שגיאה: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Delete resource
  const handleDelete = async (resource) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק משאב זה?')) return;

    try {
      console.log('Deleting resource:', resource.id);
      
      // Delete file from storage if exists
      if (resource.storagePath) {
        try {
          const fileRef = storageRef(storage, resource.storagePath);
          await deleteObject(fileRef);
          console.log('File deleted from storage');
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Continue with document deletion even if storage deletion fails
        }
      }

      // Delete document
      await deleteDoc(doc(db, 'sopResources', resource.id));
      console.log('Resource document deleted successfully');
      alert('המשאב נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('שגיאה במחיקת המשאב');
    }
  };

  // Get icon for resource type
  const getResourceIcon = (type, mime) => {
    if (type === 'link') return ExternalLink;
    if (type === 'embed') return FolderOpen;
    if (mime?.includes('video')) return Video;
    return FileText;
  };

  // Get icon color for resource type
  const getResourceColor = (type, mime) => {
    if (type === 'link') return 'blue';
    if (type === 'embed') return 'purple';
    if (mime?.includes('video')) return 'red';
    return 'gray';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">ניהול משאבים</h1>
            <p className="text-sm text-gray-600 mt-1">קבצים, מסמכים וקישורים לתהליכי SOP</p>
          </div>
          <button
            onClick={() => {
              console.log('Upload button clicked - opening modal');
              console.log('Current modal state:', showUploadModal);
              console.log('Current user:', currentUser);
              setShowUploadModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload size={20} />
            העלה משאב
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="חיפוש משאבים..."
              className="w-full pr-10 pl-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">כל הסוגים</option>
            <option value="file">קבצים</option>
            <option value="link">קישורים</option>
            <option value="embed">הטמעות</option>
          </select>

          {/* Tags Filter */}
          <div className="relative">
            <select
              onChange={(e) => {
                const tag = e.target.value;
                if (tag && !filters.tags.includes(tag)) {
                  setFilters({ ...filters, tags: [...filters.tags, tag] });
                }
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value=""
            >
              <option value="">הוסף תגית לסינון</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => setFilters({ search: '', type: 'all', tags: [], department: 'all' })}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            נקה מסננים
          </button>
        </div>

        {/* Active Tags */}
        {filters.tags.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-gray-600">תגיות פעילות:</span>
            {filters.tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
              >
                {tag}
                <button
                  onClick={() => setFilters({
                    ...filters,
                    tags: filters.tags.filter(t => t !== tag)
                  })}
                  className="hover:text-blue-900"
                >
                  <XCircle size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map(resource => {
          const Icon = getResourceIcon(resource.type, resource.mime);
          const color = getResourceColor(resource.type, resource.mime);

          return (
            <div key={resource.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg bg-${color}-50 text-${color}-600`}>
                      <Icon size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{resource.title}</h3>
                      <p className="text-sm text-gray-500">
                        {resource.type === 'file' ? 'קובץ' : 
                         resource.type === 'link' ? 'קישור' : 'הטמעה'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setSelectedResource(resource)}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title="צפה"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(resource)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="מחק"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {resource.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {resource.description}
                  </p>
                )}

                {/* Tags */}
                {resource.tags && resource.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {resource.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                      >
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-gray-500 border-t pt-3">
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    גרסה {resource.version || 1}
                  </span>
                  {resource.linkedTemplates?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <FileText size={12} />
                      {resource.linkedTemplates.length} תבניות
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredResources.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <File size={48} className="mx-auto" />
          </div>
          <p className="text-gray-600">
            {filters.search || filters.tags.length > 0 || filters.type !== 'all'
              ? 'לא נמצאו משאבים התואמים לחיפוש'
              : 'אין משאבים עדיין'}
          </p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">העלאת משאב חדש</h2>
            
            <form onSubmit={handleUpload}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    כותרת <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newResource.title}
                    onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">סוג משאב</label>
                  <select
                    value={newResource.type}
                    onChange={(e) => setNewResource({ ...newResource, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="file">קובץ</option>
                    <option value="link">קישור</option>
                    <option value="embed">הטמעה</option>
                  </select>
                </div>

                {newResource.type === 'file' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      בחר קובץ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setNewResource({ ...newResource, file: e.target.files[0] })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.avi,.mov"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, Word, Excel, PowerPoint, וידאו
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {newResource.type === 'link' ? 'כתובת URL' : 'קוד הטמעה'} 
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={newResource.type === 'link' ? 'url' : 'text'}
                      value={newResource.url}
                      onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={newResource.type === 'link' ? 'https://...' : '<iframe>...</iframe>'}
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">תיאור</label>
                  <textarea
                    value={newResource.description}
                    onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">תגיות</label>
                  <input
                    type="text"
                    value={newResource.tags.join(', ')}
                    onChange={(e) => setNewResource({
                      ...newResource,
                      tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="הפרד תגיות בפסיקים"
                  />
                </div>

                {/* Upload Progress */}
                {loading && uploadProgress > 0 && (
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>מעלה קובץ...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Cancel button clicked');
                    setShowUploadModal(false);
                    setNewResource({
                      title: '',
                      type: 'file',
                      description: '',
                      tags: [],
                      linkedTemplates: [],
                      department: '',
                      file: null,
                      url: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  disabled={loading}
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'מעלה...' : 'העלה משאב'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resource Preview Modal */}
      {selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{selectedResource.title}</h2>
              <button
                onClick={() => setSelectedResource(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={20} />
              </button>
            </div>

            {selectedResource.description && (
              <p className="text-gray-600 mb-4">{selectedResource.description}</p>
            )}

            {/* Resource Content */}
            <div className="mb-4">
              {selectedResource.type === 'file' && (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <FileText size={48} className="mx-auto" />
                  </div>
                  <p className="text-gray-600 mb-4">
                    {selectedResource.mime || 'קובץ'}
                  </p>
                  <a
                    href={selectedResource.url || selectedResource.fileData}
                    download={selectedResource.fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Download size={18} />
                    הורד קובץ
                  </a>
                </div>
              )}

              {selectedResource.type === 'link' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <a
                    href={selectedResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink size={18} />
                    {selectedResource.url}
                  </a>
                </div>
              )}

              {selectedResource.type === 'embed' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div dangerouslySetInnerHTML={{ __html: selectedResource.embedCode }} />
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">סוג:</span>
                <span>{selectedResource.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">גרסה:</span>
                <span>{selectedResource.version || 1}</span>
              </div>
              {selectedResource.tags && selectedResource.tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">תגיות:</span>
                  <div className="flex flex-wrap gap-1">
                    {selectedResource.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                      >
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedResource.createdByEmail && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">נוצר על ידי:</span>
                  <span>{selectedResource.createdByEmail}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceManager;